import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Header } from 'components/common/header/Header';
import { Button } from 'components/common/button/Button';
import './MeetingCreate.css';

const STORAGE_KEY = 'meeting_create_data';

type TravelType = 'DOMESTIC' | 'OVERSEAS';

interface Region {
    id: string;
    name: string;
    nameEn?: string;
    emoji?: string;
    description?: string;
    popular?: boolean;
}

// 국내 지역 데이터
const DOMESTIC_REGIONS: Region[] = [
    { id: 'seoul', name: '서울', emoji: '🏙️', description: '대한민국의 수도', popular: true },
    { id: 'busan', name: '부산', emoji: '🌊', description: '해운대, 광안리 해변', popular: true },
    { id: 'jeju', name: '제주', emoji: '🍊', description: '아름다운 섬 여행', popular: true },
    { id: 'gyeonggi', name: '경기', emoji: '🏞️', description: '수도권 근교 여행' },
    { id: 'gangwon', name: '강원', emoji: '⛷️', description: '설악산, 동해 바다', popular: true },
    { id: 'chungcheong', name: '충청', emoji: '🏛️', description: '대전, 공주, 부여' },
    { id: 'jeolla', name: '전라', emoji: '🍚', description: '광주, 전주, 여수', popular: true },
    { id: 'gyeongsang', name: '경상', emoji: '🏔️', description: '대구, 경주, 안동' },
];

// 해외 국가/지역 데이터
const OVERSEAS_REGIONS: Region[] = [
    { id: 'japan', name: '일본', nameEn: 'Japan', emoji: '🇯🇵', description: '도쿄, 오사카, 교토', popular: true },
    { id: 'thailand', name: '태국', nameEn: 'Thailand', emoji: '🇹🇭', description: '방콕, 푸켓, 치앙마이', popular: true },
    { id: 'vietnam', name: '베트남', nameEn: 'Vietnam', emoji: '🇻🇳', description: '다낭, 호이안, 하노이', popular: true },
    { id: 'taiwan', name: '대만', nameEn: 'Taiwan', emoji: '🇹🇼', description: '타이베이, 지우펀' },
    { id: 'singapore', name: '싱가포르', nameEn: 'Singapore', emoji: '🇸🇬', description: '마리나베이, 센토사' },
    { id: 'hongkong', name: '홍콩', nameEn: 'Hong Kong', emoji: '🇭🇰', description: '빅토리아 피크, 야경' },
    { id: 'philippines', name: '필리핀', nameEn: 'Philippines', emoji: '🇵🇭', description: '세부, 보라카이, 팔라완' },
    { id: 'usa', name: '미국', nameEn: 'USA', emoji: '🇺🇸', description: 'LA, 뉴욕, 하와이' },
    { id: 'europe', name: '유럽', nameEn: 'Europe', emoji: '🇪🇺', description: '파리, 런던, 로마' },
    { id: 'australia', name: '호주', nameEn: 'Australia', emoji: '🇦🇺', description: '시드니, 멜버른' },
];

export const TravelDestination = () => {
    const navigate = useNavigate();

    const loadSavedData = () => {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved) : {};
    };

    const savedData = loadSavedData();

    const [travelType, setTravelType] = useState<TravelType | null>(savedData.travelType || null);
    const [selectedRegion, setSelectedRegion] = useState<Region | null>(savedData.selectedRegion || null);
    const [searchQuery, setSearchQuery] = useState('');

    // 데이터 저장
    useEffect(() => {
        const dataToSave = {
            ...savedData,
            travelType,
            selectedRegion,
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    }, [travelType, selectedRegion]);

    const regions = travelType === 'DOMESTIC' ? DOMESTIC_REGIONS : OVERSEAS_REGIONS;
    const popularRegions = regions.filter(r => r.popular);
    const otherRegions = regions.filter(r => !r.popular);

    const filteredRegions = searchQuery
        ? regions.filter(r =>
            r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (r.nameEn && r.nameEn.toLowerCase().includes(searchQuery.toLowerCase()))
        )
        : null;

    const handleNext = () => {
        if (selectedRegion) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
                ...savedData,
                travelType,
                selectedRegion,
                destination: selectedRegion.name,
                destinationInfo: {
                    name: selectedRegion.name,
                    type: travelType,
                    regionId: selectedRegion.id,
                }
            }));
            navigate('/meeting/create/travel');
        }
    };

    const isNextEnabled = selectedRegion !== null;

    return (
        <div className="meeting-create-wrapper">
            <Header title="여행지 선택" isSubPage />

            <div className="meeting-create-content">
                {/* Progress Indicator */}
                <div className="progress-indicator">
                    <div className="progress-step completed">1</div>
                    <div className="progress-line active"></div>
                    <div className="progress-step completed">2</div>
                    <div className="progress-line active"></div>
                    <div className="progress-step active">3</div>
                </div>

                <div className="progress-labels three-steps">
                    <span className="progress-label completed">모임 유형</span>
                    <span className="progress-label completed">모임 정보</span>
                    <span className="progress-label active">일정 설정</span>
                </div>

                {/* 국내/해외 선택 */}
                {!travelType && (
                    <>
                        <div className="meeting-create-title-section">
                            <div className="meeting-type-badge">✈️ 여행</div>
                            <h1 className="meeting-create-title">어디로 떠나시나요?</h1>
                            <p className="meeting-create-subtitle">
                                국내 여행과 해외 여행 중 선택해주세요.
                            </p>
                        </div>

                        <div className="travel-type-buttons">
                            <button
                                className="travel-type-button"
                                onClick={() => setTravelType('DOMESTIC')}
                            >
                                <span className="travel-type-emoji">🇰🇷</span>
                                <span className="travel-type-name">국내 여행</span>
                                <span className="travel-type-desc">제주, 부산, 강원 등</span>
                            </button>
                            <button
                                className="travel-type-button"
                                onClick={() => setTravelType('OVERSEAS')}
                            >
                                <span className="travel-type-emoji">🌏</span>
                                <span className="travel-type-name">해외 여행</span>
                                <span className="travel-type-desc">일본, 태국, 베트남 등</span>
                            </button>
                        </div>
                    </>
                )}

                {/* 지역 선택 */}
                {travelType && !selectedRegion && (
                    <>
                        <div className="meeting-create-title-section">
                            <button
                                className="back-to-type"
                                onClick={() => setTravelType(null)}
                            >
                                ← {travelType === 'DOMESTIC' ? '국내 여행' : '해외 여행'}
                            </button>
                            <h1 className="meeting-create-title">
                                {travelType === 'DOMESTIC' ? '어느 지역으로 떠나시나요?' : '어느 나라로 떠나시나요?'}
                            </h1>
                        </div>

                        {/* 검색 */}
                        <div className="destination-search">
                            <input
                                type="text"
                                className="destination-search-input"
                                placeholder={travelType === 'DOMESTIC' ? '지역 검색...' : '국가/도시 검색...'}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button
                                    className="search-clear-btn"
                                    onClick={() => setSearchQuery('')}
                                >
                                    ✕
                                </button>
                            )}
                        </div>

                        {filteredRegions ? (
                            /* 검색 결과 */
                            <div className="region-grid">
                                {filteredRegions.map(region => (
                                    <button
                                        key={region.id}
                                        className="region-card"
                                        onClick={() => setSelectedRegion(region)}
                                    >
                                        <span className="region-emoji">{region.emoji}</span>
                                        <span className="region-name">{region.name}</span>
                                        {region.nameEn && <span className="region-name-en">{region.nameEn}</span>}
                                    </button>
                                ))}
                                {filteredRegions.length === 0 && (
                                    <div className="no-results">검색 결과가 없습니다.</div>
                                )}
                            </div>
                        ) : (
                            <>
                                {/* 인기 지역 */}
                                <div className="region-section">
                                    <h3 className="region-section-title">🔥 인기 여행지</h3>
                                    <div className="region-grid">
                                        {popularRegions.map(region => (
                                            <button
                                                key={region.id}
                                                className="region-card popular"
                                                onClick={() => setSelectedRegion(region)}
                                            >
                                                <span className="region-emoji">{region.emoji}</span>
                                                <span className="region-name">{region.name}</span>
                                                <span className="region-desc">{region.description}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* 기타 지역 */}
                                {otherRegions.length > 0 && (
                                    <div className="region-section">
                                        <h3 className="region-section-title">
                                            {travelType === 'DOMESTIC' ? '기타 지역' : '기타 국가'}
                                        </h3>
                                        <div className="region-grid compact">
                                            {otherRegions.map(region => (
                                                <button
                                                    key={region.id}
                                                    className="region-card compact"
                                                    onClick={() => setSelectedRegion(region)}
                                                >
                                                    <span className="region-emoji">{region.emoji}</span>
                                                    <span className="region-name">{region.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}

                {/* 선택된 지역 - 확인 후 다음 단계로 */}
                {selectedRegion && (
                    <>
                        <div className="meeting-create-title-section">
                            <button
                                className="back-to-type"
                                onClick={() => setSelectedRegion(null)}
                            >
                                ← 다른 지역 선택
                            </button>
                            <div className="selected-destination-header">
                                <span className="destination-emoji">{selectedRegion.emoji}</span>
                                <div className="destination-info">
                                    <h1 className="meeting-create-title">{selectedRegion.name}</h1>
                                    <p className="meeting-create-subtitle">{selectedRegion.description}</p>
                                </div>
                            </div>
                        </div>

                        {/* 선택 확인 카드 */}
                        <div className="region-confirm-card">
                            <div className="confirm-icon">✓</div>
                            <div className="confirm-content">
                                <div className="confirm-title">
                                    {travelType === 'OVERSEAS' ? '해외 여행' : '국내 여행'} - {selectedRegion.name}
                                </div>
                                <div className="confirm-desc">
                                    다음 단계에서 여행 기간과 집결 장소를 설정해요.
                                </div>
                            </div>
                        </div>

                        {/* 여행 정보 */}
                        <div className="travel-info-box">
                            <h3 className="info-box-title">💡 {selectedRegion.name} 여행 팁</h3>
                            <ul className="info-box-list">
                                {travelType === 'OVERSEAS' ? (
                                    <>
                                        <li>비자 및 여권 유효기간을 확인하세요.</li>
                                        <li>현지 날씨와 시차를 미리 체크하세요.</li>
                                        <li>환전 및 현지 결제 수단을 준비하세요.</li>
                                    </>
                                ) : (
                                    <>
                                        <li>성수기에는 숙소를 미리 예약하세요.</li>
                                        <li>대중교통이나 렌터카 정보를 확인하세요.</li>
                                        <li>지역 맛집과 숨은 명소를 찾아보세요.</li>
                                    </>
                                )}
                            </ul>
                        </div>
                    </>
                )}
            </div>

            {/* Next Button - 지역 선택 후에만 표시 */}
            {selectedRegion && (
                <div className="meeting-create-footer">
                    <Button
                        text="일정 설정하기"
                        disabled={!isNextEnabled}
                        onPress={handleNext}
                    />
                </div>
            )}
        </div>
    );
};
